import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { Users, CheckCircle, Ban, Wallet, Package, X, Eye } from 'lucide-react';

const WALLET_OPTIONS = [
  { value: 'roiWallet', label: 'ROI Wallet' },
  { value: 'directWallet', label: 'Direct Wallet' },
  { value: 'levelWallet', label: 'Level Wallet' },
  { value: 'royaltyWallet', label: 'Royalty Wallet' },
  { value: 'rewardWallet', label: 'Reward Wallet' },
  { value: 'walletBalance', label: 'Bonus Wallet' },
];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 15;

  // Adjustment modal state
  const [modalUser, setModalUser] = useState(null);
  const [modalTab, setModalTab] = useState('wallet');
  const [walletForm, setWalletForm] = useState({ walletType: 'roiWallet', type: 'credit', amount: '', note: '' });
  const [pkgForm, setPkgForm] = useState({ amount: '', validityDays: '365', note: '' });
  const [submitting, setSubmitting] = useState(false);

  // View details modal state
  const [detailUser, setDetailUser] = useState(null);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: LIMIT });
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    api.get(`/users/admin/all?${params}`)
      .then(r => { setUsers(r.data.users); setTotal(r.data.total); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, search, status]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleBlock = async (userId, isBlocked) => {
    if (!window.confirm(`${isBlocked ? 'Unblock' : 'Block'} this user?`)) return;
    try {
      const res = await api.put(`/users/admin/${userId}/block`);
      toast.success(res.data.message);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const handleToggleRoi = async (userId, currentlyStopped) => {
    if (!window.confirm(`${currentlyStopped ? 'Resume' : 'Stop'} daily ROI for this user?`)) return;
    try {
      const res = await api.put(`/users/admin/${userId}/roi-stop`);
      toast.success(res.data.message);
      fetchUsers();
      // Keep the details modal in sync if it's open for this user
      setDetailUser(prev => (prev && prev._id === userId) ? { ...prev, roiStopped: res.data.roiStopped } : prev);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update ROI status');
    }
  };

  const openModal = (user) => {
    setModalUser(user);
    setModalTab('wallet');
    setWalletForm({ walletType: 'roiWallet', type: 'credit', amount: '', note: '' });
    setPkgForm({ amount: '', validityDays: '365', note: '' });
  };

  const closeModal = () => setModalUser(null);

  const handleWalletSubmit = async (e) => {
    e.preventDefault();
    if (!walletForm.amount || Number(walletForm.amount) === 0) {
      toast.error('Enter a non-zero amount'); return;
    }
    if (!walletForm.note.trim()) {
      toast.error('Reason/note is required'); return;
    }
    setSubmitting(true);
    const signedAmount = walletForm.type === 'debit'
      ? -Math.abs(Number(walletForm.amount))
      : Math.abs(Number(walletForm.amount));
    try {
      const res = await api.put(`/users/admin/${modalUser._id}/wallet`, {
        walletType: walletForm.walletType,
        amount: signedAmount,
        note: walletForm.note,
      });
      toast.success(res.data.message);
      closeModal();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to adjust wallet');
    } finally { setSubmitting(false); }
  };

  const handlePackageActivate = async (e) => {
    e.preventDefault();
    if (!pkgForm.amount || Number(pkgForm.amount) < 500) {
      toast.error('Package amount must be at least ₹500'); return;
    }
    if (!pkgForm.note.trim()) {
      toast.error('Reason/note is required'); return;
    }
    setSubmitting(true);
    try {
      const res = await api.put(`/users/admin/${modalUser._id}/package`, pkgForm);
      toast.success(res.data.message);
      closeModal();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to activate package');
    } finally { setSubmitting(false); }
  };

  const handlePackageRevoke = async () => {
    if (!window.confirm(`Revoke ${modalUser.name}'s active package? This deactivates their account and stops ROI.`)) return;
    setSubmitting(true);
    try {
      const res = await api.put(`/users/admin/${modalUser._id}/package`, { action: 'revoke', note: pkgForm.note });
      toast.success(res.data.message);
      closeModal();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to revoke package');
    } finally { setSubmitting(false); }
  };

  const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
  const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <DashboardLayout>
      <h1 className="page-title">All Users</h1>
      <p className="page-subtitle">Manage registered users — Total: {total}</p>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
            <label>Search</label>
            <input type="text" className="form-control" placeholder="Name, email, mobile, referral code..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <div className="form-group" style={{ minWidth: 160, marginBottom: 0 }}>
            <label>Status</label>
            <select className="form-control" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All Users</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
          <button className="btn btn-outline btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={() => { setSearch(''); setStatus(''); setPage(1); }}>Reset</button>
        </div>
      </div>

      <div className="card">
        {loading ? <div className="loader-wrap"><div className="loader"></div></div> :
          users.length === 0 ? (
            <div className="empty-state"><div className="empty-icon"><Users size={40} /></div><h3>No users found</h3></div>
          ) : (
            <>
            <div className="table-wrap">
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>#</th><th>Name</th><th>Email</th><th>Mobile</th><th>Ref Code</th>
                    <th>Sponsor</th><th>Package</th><th>Earned</th><th>Status</th><th>Joined</th><th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => (
                    <tr key={u._id} style={u.isBlocked ? { opacity: 0.5 } : {}}>
                      <td style={{ color: 'var(--text-muted)' }}>{(page - 1) * LIMIT + i + 1}</td>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{u.name}</td>
                      <td style={{ fontSize: '0.82rem', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.email}>{u.email}</td>
                      <td style={{ fontSize: '0.85rem' }}>{u.mobile}</td>
                      <td><span style={{ color: 'var(--primary)', fontFamily: 'monospace', letterSpacing: '1px', fontSize: '0.85rem' }}>{u.referralCode}</span></td>
                      <td style={{ fontSize: '0.82rem' }}>{u.sponsor?.referralCode || '—'}</td>
                      <td style={{ color: 'var(--primary)', fontWeight: 700 }}>
                        {u.activePackage > 0 ? formatCurrency(u.activePackage) : '—'}
                      </td>
                      <td style={{ color: '#22c55e', fontSize: '0.85rem' }}>{formatCurrency(u.totalEarned)}</td>
                      <td>
                        <span className={`badge ${u.isBlocked ? 'badge-danger' : u.isActive ? 'badge-success' : 'badge-warning'}`}>
                          {u.isBlocked ? 'Blocked' : u.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{new Date(u.createdAt).toLocaleDateString('en-IN')}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => setDetailUser(u)}
                            title="View full details"
                            style={{ fontSize: '0.75rem', padding: '6px 10px', flexShrink: 0 }}
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => openModal(u)}
                            style={{ fontSize: '0.75rem', padding: '6px 10px', flexShrink: 0 }}
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}><Wallet size={14} /> Adjust</span>
                          </button>
                          <button
                            className={`btn btn-sm ${u.isBlocked ? 'btn-success' : 'btn-danger'}`}
                            onClick={() => handleBlock(u._id, u.isBlocked)}
                            style={{ fontSize: '0.75rem', padding: '6px 12px', flexShrink: 0 }}
                          >
                            {u.isBlocked ? (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}><CheckCircle size={14} /> Unblock</span>) : (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}><Ban size={14} /> Block</span>)}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card view — shown instead of the table below 768px (see .tp-cards CSS) */}
            <div className="tp-cards">
              {users.map((u, i) => (
                <div key={u._id} className="tp-card" style={u.isBlocked ? { opacity: 0.5 } : {}}>
                  <div className="tp-card-head">
                    <div>
                      <div className="tp-card-name">#{(page - 1) * LIMIT + i + 1} · {u.name}</div>
                      <div className="tp-card-sub">{u.email}</div>
                      <div className="tp-card-sub">{u.mobile}</div>
                    </div>
                    <span className={`badge ${u.isBlocked ? 'badge-danger' : u.isActive ? 'badge-success' : 'badge-warning'}`}>
                      {u.isBlocked ? 'Blocked' : u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="tp-card-body">
                    <div className="tp-card-field">
                      <span className="label">Ref Code</span>
                      <span className="value" style={{ color: 'var(--primary)', fontFamily: 'monospace' }}>{u.referralCode}</span>
                    </div>
                    <div className="tp-card-field">
                      <span className="label">Sponsor</span>
                      <span className="value">{u.sponsor?.referralCode || '—'}</span>
                    </div>
                    <div className="tp-card-field">
                      <span className="label">Package</span>
                      <span className="value" style={{ color: 'var(--primary)' }}>
                        {u.activePackage > 0 ? formatCurrency(u.activePackage) : '—'}
                      </span>
                    </div>
                    <div className="tp-card-field">
                      <span className="label">Earned</span>
                      <span className="value" style={{ color: '#22c55e' }}>{formatCurrency(u.totalEarned)}</span>
                    </div>
                    <div className="tp-card-field">
                      <span className="label">Joined</span>
                      <span className="value">{new Date(u.createdAt).toLocaleDateString('en-IN')}</span>
                    </div>
                  </div>

                  <div className="tp-card-actions">
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => setDetailUser(u)}
                      title="View full details"
                      style={{ fontSize: '0.75rem', padding: '8px 10px' }}
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => openModal(u)}
                      style={{ fontSize: '0.75rem', padding: '8px 10px' }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Wallet size={14} /> Adjust</span>
                    </button>
                    <button
                      className={`btn btn-sm ${u.isBlocked ? 'btn-success' : 'btn-danger'}`}
                      onClick={() => handleBlock(u._id, u.isBlocked)}
                      style={{ fontSize: '0.75rem', padding: '8px 10px' }}
                    >
                      {u.isBlocked ? (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle size={14} /> Unblock</span>) : (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Ban size={14} /> Block</span>)}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            </>
          )
        }

        {totalPages > 1 && (
          <div className="pagination">
            <button className="page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const p = i + Math.max(1, page - 2);
              if (p > totalPages) return null;
              return <button key={p} className={`page-btn ${page === p ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>;
            })}
            <button className="page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
          </div>
        )}
      </div>

      {/* Adjustment Modal */}
      {modalUser && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: 16
          }}
          onClick={closeModal}
        >
          <div
            className="card"
            style={{ maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 className="card-title">Adjust — {modalUser.name}</h3>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{modalUser.referralCode} · {modalUser.email}</div>
              </div>
              <button onClick={closeModal} className="btn btn-outline btn-sm" style={{ padding: 6 }}><X size={16} /></button>
            </div>

            <div className="tab-bar" style={{ marginBottom: 16 }}>
              <button className={`tab-btn ${modalTab === 'wallet' ? 'active' : ''}`} onClick={() => setModalTab('wallet')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Wallet size={14} /> Wallet
              </button>
              <button className={`tab-btn ${modalTab === 'package' ? 'active' : ''}`} onClick={() => setModalTab('package')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Package size={14} /> Package
              </button>
            </div>

            {modalTab === 'wallet' ? (
              <form onSubmit={handleWalletSubmit}>
                <div className="form-group">
                  <label>Wallet</label>
                  <select
                    className="form-control"
                    value={walletForm.walletType}
                    onChange={e => setWalletForm(f => ({ ...f, walletType: e.target.value }))}
                  >
                    {WALLET_OPTIONS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Adjustment Type</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => setWalletForm(f => ({ ...f, type: 'credit' }))}
                      style={{
                        flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                        border: walletForm.type === 'credit' ? '1px solid #22c55e' : '1px solid #374151',
                        background: walletForm.type === 'credit' ? 'rgba(34,197,94,0.15)' : 'transparent',
                        color: walletForm.type === 'credit' ? '#22c55e' : 'var(--text-muted)',
                        fontWeight: 600,
                      }}
                    >
                      + Credit
                    </button>
                    <button
                      type="button"
                      onClick={() => setWalletForm(f => ({ ...f, type: 'debit' }))}
                      style={{
                        flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                        border: walletForm.type === 'debit' ? '1px solid #ef4444' : '1px solid #374151',
                        background: walletForm.type === 'debit' ? 'rgba(239,68,68,0.15)' : 'transparent',
                        color: walletForm.type === 'debit' ? '#ef4444' : 'var(--text-muted)',
                        fontWeight: 600,
                      }}
                    >
                      − Debit
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Amount (₹)</label>
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    placeholder="e.g. 1000"
                    value={walletForm.amount}
                    onChange={e => setWalletForm(f => ({ ...f, amount: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Reason / Note (required)</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Why is this adjustment being made?"
                    value={walletForm.note}
                    onChange={e => setWalletForm(f => ({ ...f, note: e.target.value }))}
                  />
                </div>
                <button type="submit" className="btn btn-success" disabled={submitting} style={{ width: '100%' }}>
                  {submitting ? 'Saving...' : 'Apply Adjustment'}
                </button>
              </form>
            ) : (
              <div>
                <form onSubmit={handlePackageActivate}>
                  <div className="form-group">
                    <label>Package Amount (₹)</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Minimum ₹500"
                      value={pkgForm.amount}
                      onChange={e => setPkgForm(f => ({ ...f, amount: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Validity (days)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={pkgForm.validityDays}
                      onChange={e => setPkgForm(f => ({ ...f, validityDays: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Reason / Note (required)</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Why is this package being manually activated?"
                      value={pkgForm.note}
                      onChange={e => setPkgForm(f => ({ ...f, note: e.target.value }))}
                    />
                  </div>
                  <button type="submit" className="btn btn-success" disabled={submitting} style={{ width: '100%', marginBottom: 10 }}>
                    {submitting ? 'Saving...' : 'Activate Package'}
                  </button>
                </form>
                {modalUser.activePackage > 0 && (
                  <button
                    type="button"
                    onClick={handlePackageRevoke}
                    disabled={submitting}
                    className="btn btn-danger"
                    style={{ width: '100%' }}
                  >
                    Revoke Current Package (₹{modalUser.activePackage.toLocaleString('en-IN')})
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* View Full Details Modal */}
      {detailUser && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: 16
          }}
          onClick={() => setDetailUser(null)}
        >
          <div
            className="card"
            style={{ maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 className="card-title">{detailUser.name}</h3>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{detailUser.referralCode} · User ID: {detailUser._id}</div>
              </div>
              <button onClick={() => setDetailUser(null)} className="btn btn-outline btn-sm" style={{ padding: 6 }}><X size={16} /></button>
            </div>

            {/* Basic Info */}
            <div style={{ marginTop: 8, marginBottom: 20 }}>
              <h4 style={{ color: 'var(--primary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Basic Info</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
                <DetailField label="Full Name" value={detailUser.name} />
                <DetailField label="Email" value={detailUser.email} />
                <DetailField label="Mobile" value={detailUser.mobile} />
                <DetailField label="Referral Code" value={detailUser.referralCode} mono />
                <DetailField label="Sponsor Code" value={detailUser.sponsorCode || '—'} mono />
                <DetailField label="Sponsor" value={detailUser.sponsor ? `${detailUser.sponsor.name} (${detailUser.sponsor.referralCode})` : '—'} />
                <DetailField label="Role" value={detailUser.role} />
                <DetailField label="Joined On" value={formatDateTime(detailUser.createdAt)} />
                <DetailField label="Last Updated" value={formatDateTime(detailUser.updatedAt)} />
              </div>
            </div>

            {/* Account Status */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ color: 'var(--primary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Account Status</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
                <DetailField label="Account Status" value={detailUser.isBlocked ? 'Blocked' : detailUser.isActive ? 'Active' : 'Inactive'} />
                <DetailField label="KYC Status" value={detailUser.kycStatus?.replace('_', ' ') || 'not submitted'} />
                <DetailField label="ROI Status" value={detailUser.roiStopped ? 'Stopped' : 'Running'} highlight={!detailUser.roiStopped} green={!detailUser.roiStopped} />
              </div>
            </div>

            {/* Package Info */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ color: 'var(--primary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Package Info</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px', marginBottom: 12 }}>
                <DetailField label="Active Package" value={detailUser.activePackage > 0 ? formatCurrency(detailUser.activePackage) : '—'} highlight />
                <DetailField label="Activated On" value={formatDateTime(detailUser.packageActivatedAt)} />
                <DetailField label="Expires On" value={formatDateTime(detailUser.packageExpiresAt)} />
                <DetailField label="Last ROI Date" value={formatDateTime(detailUser.lastRoiDate)} />
              </div>
              <button
                type="button"
                className={`btn btn-sm ${detailUser.roiStopped ? 'btn-success' : 'btn-danger'}`}
                onClick={() => handleToggleRoi(detailUser._id, detailUser.roiStopped)}
                style={{ width: '100%' }}
              >
                {detailUser.roiStopped ? 'Resume ROI for this User' : 'Stop ROI for this User'}
              </button>
            </div>

            {/* Wallets */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ color: 'var(--primary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Wallets</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
                <DetailField label="Bonus Wallet" value={formatCurrency(detailUser.walletBalance)} green />
                <DetailField label="ROI Wallet" value={formatCurrency(detailUser.roiWallet)} green />
                <DetailField label="Direct Wallet" value={formatCurrency(detailUser.directWallet)} green />
                <DetailField label="Level Wallet" value={formatCurrency(detailUser.levelWallet)} green />
                <DetailField label="Royalty Wallet" value={formatCurrency(detailUser.royaltyWallet)} green />
                <DetailField label="Reward Wallet" value={formatCurrency(detailUser.rewardWallet)} green />
                <DetailField label="Total Earned" value={formatCurrency(detailUser.totalEarned)} green />
                <DetailField label="Total Withdrawn" value={formatCurrency(detailUser.totalWithdrawn)} />
              </div>
            </div>

            {/* Team Stats */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ color: 'var(--primary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Team & Business</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
                <DetailField label="Total Directs" value={detailUser.totalDirects ?? 0} />
                <DetailField label="Total Team Size" value={detailUser.totalTeam ?? 0} />
                <DetailField label="Total Business" value={formatCurrency(detailUser.totalBusiness)} />
                <DetailField label="Royalty Level" value={detailUser.royaltyLevel ?? 0} />
                <DetailField label="Royalty Achieved On" value={formatDateTime(detailUser.royaltyLevelAchievedAt)} />
                <DetailField label="Reward Level" value={detailUser.rewardLevel ?? 0} />
              </div>
            </div>

            {/* Bank Details */}
            <div style={{ marginBottom: 8 }}>
              <h4 style={{ color: 'var(--primary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Bank Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
                <DetailField label="Account Holder" value={detailUser.bankDetails?.accountName || '—'} />
                <DetailField label="Account Number" value={detailUser.bankDetails?.accountNumber || '—'} mono />
                <DetailField label="IFSC Code" value={detailUser.bankDetails?.ifscCode || '—'} mono />
                <DetailField label="Bank Name" value={detailUser.bankDetails?.bankName || '—'} />
                <DetailField label="UPI ID" value={detailUser.bankDetails?.upiId || '—'} mono />
                <DetailField label="USDT Address" value={detailUser.bankDetails?.usdtAddress || '—'} mono />
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function DetailField({ label, value, mono, green, highlight }) {
  return (
    <div>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>{label}</div>
      <div
        style={{
          fontSize: '0.9rem',
          fontFamily: mono ? 'monospace' : 'inherit',
          color: highlight ? 'var(--primary)' : green ? '#22c55e' : 'var(--text-white)',
          fontWeight: highlight || green ? 600 : 400,
          wordBreak: 'break-all',
        }}
      >
        {value === undefined || value === null || value === '' ? '—' : value}
      </div>
    </div>
  );
}